/* eslint-disable no-unused-vars */
import { useAppDispatch } from "@store/hooks";
import {
	type OpenErrorToastAction,
	type OpenInfoToastAction,
	type OpenSuccessToastAction,
	type OpenToastAction,
	type OpenWarningToastAction,
	openErrorToast,
	openInfoToast,
	openSuccessToast,
	openToast,
	openWarningToast,
} from "@store/slices/toast";

type ContractFuntionActionName =
	| "Merge"
	| "Create"
	| "Claim"
	| "Compound"
	| "Migrate"
	| "Withdraw";

const DEFAULT_ERROR_MESSAGE = "Check your wallet and try again.";

interface UseToastReturn {
	toast: (props: OpenToastAction) => void;
	successToast: (props: OpenSuccessToastAction) => void;
	warningToast: (props: OpenWarningToastAction) => void;
	errorToast: (props: OpenErrorToastAction) => void;
	infoToast: (props: OpenInfoToastAction) => void;
	contractFunctionToast: (
		actionName: ContractFuntionActionName,
		transactionStatus?: string,
	) => void;
}

const useToast = (): UseToastReturn => {
	const dispatch = useAppDispatch();
	const toast = (props: OpenToastAction) => dispatch(openToast(props));
	const successToast = (props: OpenSuccessToastAction) =>
		dispatch(openSuccessToast(props));
	const warningToast = (props: OpenWarningToastAction) =>
		dispatch(openWarningToast(props));
	const errorToast = (props: OpenErrorToastAction) =>
		dispatch(openErrorToast(props));
	const infoToast = (props: OpenInfoToastAction) =>
		dispatch(openInfoToast(props));

	/**
	 * Toaster for `useContractFunction` hook state
	 * The state of this hook will feed the function.
	 */
	const contractFunctionToast = (
		actionName: ContractFuntionActionName,
		transactionStatus?: string,
	) => {
		switch (transactionStatus) {
			case "Fail":
			case "Exception": {
				errorToast({
					message: `[${actionName} Error] ${
						transactionStatus ?? DEFAULT_ERROR_MESSAGE
					}`,
				});
				break;
			}
			case "Mining": {
				infoToast({
					message: `[${actionName}] Mining...`,
				});
				break;
			}
			case "Success": {
				successToast({
					message: `[${actionName}] Success!`,
				});
				break;
			}
			default: {
				break;
			}
		}
	};

	return {
		contractFunctionToast,
		errorToast,
		infoToast,
		successToast,
		toast,
		warningToast,
	};
};

export default useToast;
